/**
 * fitment-deeplinks.js — a linkable URL for every eyepiece fitment AND every
 * phone adapter.
 *
 * WHY (Aug 20 2026, extended to the adapter Aug 22): both Mountain Mag product
 * pages build their picker in client-side JS (brand buttons -> a <select> of
 * fitments / phone models), and neither picker reads the URL. Verified against
 * the live pages: `?variant=<id>` changes the rendered page by exactly nothing,
 * so a CS reply cannot point a customer at their part — it can only say "pick
 * your brand, then find your model", which is the round trip the ticket
 * already was. Same problem the FAQ had, same shape of fix (theme-edits/faq-anchors).
 *
 * IN:  eyepiece  ?variant=<id>&optic=<slug>
 *      adapter   ?variant=<id>&phone=<slug>
 *      selects that option on load — switching the brand filter first, because
 *      each picker hard-defaults to one brand (Vortex / iPhone) and a Kowa or a
 *      Pixel is not in the list until its button is clicked. Either param alone
 *      also works.
 *
 *      SLUG FIRST, ID FALLBACK, and both halves earn their place. The slug is
 *      what disambiguates ALIASES — one variant id, several display names
 *      (44 of the 151 eyepiece options; `iPhone 16e` / `iPhone 17e` on the
 *      adapter) — so an id alone cannot say which one the customer asked about.
 *      But the slug is derived from display text, the most-edited string in
 *      the catalog, so it is the half that rots: rename an option in the admin
 *      and every link already sent carries a slug that matches nothing. The id
 *      survives that. Trying exact, then slug, then id means a rename costs the
 *      alias precision and nothing else — the customer still lands on their part.
 * OUT: choosing an option writes its link into the address bar, so getting a
 *      customer's link is click-and-copy instead of a lookup.
 *
 * IT DRIVES THE UI, IT DOES NOT REACH INSIDE IT. Everything each inline picker
 * owns — the variant list, the alias map, the current filter, updateDropdown —
 * is closure-scoped and unreachable from here on purpose. This clicks the real
 * brand buttons and reads the real <option> list, so it cannot drift from the
 * picker's own matching rules, and it keeps working when a name is edited in
 * the admin. Nothing in either inline block needs to change; the same one line
 * in each template loads this file.
 *
 * HOW IT TELLS THE TWO PAGES APART (and why it barely has to): both pickers
 * render the brand row as `.switcher button[data-filter]` and the list as
 * `#variant-select`, so the driving code is identical. The only per-page
 * difference is the NAME of the slug parameter, chosen so a link reads as what
 * it is (`optic=` vs `phone=`): the adapter's form carries Bruce's id
 * `filtered-dropdown-add-to-cart-form`, the eyepiece's does not. Both names
 * are accepted on the way IN regardless.
 *
 * Deliberately never clicks a TYPE button (eyepiece only). `currentType` starts
 * empty (all types shown) and the picker has no "All" button to clear it again
 * — so leaving type alone both keeps the option list wide and routes around the
 * known bug where `Solo (Vortex Mono)` matches neither type and vanishes on the
 * first type click. A deep link reaches the Solo; the buttons still cannot.
 */
(function () {
  'use strict';

  var OOS_SUFFIX = /\s+-\s+\([^()]*\)\s*$/;   // any language's " - (out of stock)" tail
  var MAX_WAIT_FRAMES = 20;
  var SLUG_PARAMS = ['optic', 'phone'];                              // accepted on the way in

  /**
   * Shopify's `handleize`, matched by scripts/fitment_links.py so the slugs agree.
   * Strips combining marks by the any-mark property escape rather than by a
   * code-point range: it is the closer match to the Python side's
   * `unicodedata.combining()` test, and it keeps this file free of backslash-u
   * escapes, which do not survive every theme-push path byte-intact (Aug 20
   * 2026 — a push landed 8 bytes short and failed its checksum because of one).
   */
  function handleize(text) {
    return String(text == null ? '' : text)
      .normalize('NFKD')
      .replace(/\p{M}/gu, '')
      .toLowerCase()
      .replace(/\+/g, '-plus')             // "S23+" must not collide with "S23" (found Aug 22 2026)
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /** The display name, without the "- (out of stock)" the pickers append. */
  function optionName(option) {
    return option.getAttribute('data-name') ||
           option.textContent.replace(OOS_SUFFIX, '').trim();
  }

  /** Which slug parameter this page WRITES: the adapter form id is the tell. */
  function slugParam() {
    return document.getElementById('filtered-dropdown-add-to-cart-form') ? 'phone' : 'optic';
  }

  function findOption(select, wantId, wantSlug) {
    var options = select.options;
    for (var i = 0; i < options.length; i++) {
      var option = options[i];
      if (!option.value) continue;                                   // a "Select a variant..." row
      if (wantId && option.value !== wantId) continue;
      if (wantSlug && handleize(optionName(option)) !== wantSlug) continue;
      return option;
    }
    return null;
  }

  /** Walk the brand buttons until this attempt's option is in the list. */
  function sweep(select, brandButtons, startingBrand, wantId, wantSlug) {
    var hit = findOption(select, wantId, wantSlug);
    for (var i = 0; !hit && i < brandButtons.length; i++) {
      if (brandButtons[i] === startingBrand) continue;
      brandButtons[i].click();                                       // the picker rebuilds the <select>
      hit = findOption(select, wantId, wantSlug);
    }
    return hit;
  }

  /**
   * Select the linked option and let the picker's own change handler do the
   * rest (photo, cart button). Returns null — and puts the brand filter back
   * where it started — if nothing matches, so a stale link degrades silently to
   * the ordinary page rather than to a broken one.
   */
  function selectDeepLinked(select, brandButtons, wantId, wantSlug) {
    var startingBrand = null;
    for (var b = 0; b < brandButtons.length; b++) {
      if (brandButtons[b].classList.contains('active')) { startingBrand = brandButtons[b]; break; }
    }

    var attempts = (wantId && wantSlug)
      ? [[wantId, wantSlug], ['', wantSlug], [wantId, '']]           // exact, then slug, then id
      : [[wantId, wantSlug]];

    var hit = null;
    for (var a = 0; !hit && a < attempts.length; a++) {
      if (a) {
        if (startingBrand) startingBrand.click();                    // reset before re-sweeping
        console.info('fitment deep link: no exact match, retrying with ' +
                     (attempts[a][0] ? 'variant id only (was the option renamed?)' : 'slug only'));
      }
      hit = sweep(select, brandButtons, startingBrand, attempts[a][0], attempts[a][1]);
    }

    if (!hit) {
      if (startingBrand) startingBrand.click();
      return null;
    }

    hit.selected = true;
    if (select.value !== hit.value) {
      // Out-of-stock options are rendered disabled. Selecting one programmatically
      // is legal, but do not bet the link on every engine agreeing: show the
      // customer the right part, still un-buyable and still labelled out of stock.
      hit.disabled = false;
      hit.selected = true;
      hit.disabled = true;
    }
    select.dispatchEvent(new Event('change'));
    return hit;
  }

  function reveal(element) {
    var box = element.getBoundingClientRect();
    var viewport = window.innerHeight || document.documentElement.clientHeight;
    if (box.top >= 0 && box.bottom <= viewport) return;              // already on screen
    element.scrollIntoView({ block: 'center' });                     // instant: a smooth scroll on load reads as a glitch
  }

  /** replaceState, not pushState — the back button must still leave the page. */
  function writeUrl(select) {
    var option = select.options[select.selectedIndex];
    var params = new URLSearchParams(window.location.search);        // utm_*, preview_theme_id etc. survive
    var name = slugParam();

    // never leave the OTHER page's slug name behind on a shared/edited URL
    for (var i = 0; i < SLUG_PARAMS.length; i++) {
      if (SLUG_PARAMS[i] !== name) params.delete(SLUG_PARAMS[i]);
    }
    if (option && option.value) {
      params.set('variant', option.value);
      params.set(name, handleize(optionName(option)));
    } else {
      params.delete('variant');
      params.delete(name);
    }

    var query = params.toString();
    history.replaceState(null, '',
      window.location.pathname + (query ? '?' + query : '') + window.location.hash);
  }

  function init() {
    var select = document.getElementById('variant-select');
    var brandButtons = document.querySelectorAll('.switcher button[data-filter]');
    if (!select || !brandButtons.length || select.dataset.deeplinksReady) return;

    // Each inline picker fills the <select> on DOMContentLoaded. This script is
    // parsed after it, so its listener runs after — but wait rather than deep
    // link into an empty list if that ever stops being true.
    if (!select.options.length) {
      if (init.waited++ < MAX_WAIT_FRAMES) requestAnimationFrame(init);
      return;
    }
    select.dataset.deeplinksReady = '1';

    var params = new URLSearchParams(window.location.search);
    var wantId = (params.get('variant') || '').trim();
    var wantSlug = '';
    for (var i = 0; i < SLUG_PARAMS.length && !wantSlug; i++) {
      wantSlug = handleize(params.get(SLUG_PARAMS[i]) || '');
    }

    var hit = (wantId || wantSlug)
      ? selectDeepLinked(select, brandButtons, wantId, wantSlug)
      : null;

    select.addEventListener('change', function () { writeUrl(select); });

    // The address bar says what the page shows (Kade, Aug 22 2026): a deep link
    // is normalised (an id-only link gains its slug), and a picker that
    // pre-selects a real option on load — the adapter opens on the first
    // iPhone — gets that selection written too. The eyepiece opens on a
    // "Select a variant..." placeholder (empty value), so its URL stays bare.
    if (hit || select.value) writeUrl(select);
    // Reveal the SELECTION, not the brand row: on a phone the eyepiece's 19
    // brand buttons stack into a column taller than the screen, so centring
    // the row left the chosen fitment and its photo below the fold (caught by
    // the mobile audit, Aug 22 2026). Centring the <select> shows the name and,
    // on the eyepiece, the photo sitting just above it.
    if (hit) reveal(select);
  }
  init.waited = 0;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
